import React, { CSSProperties } from 'react'
import { DirtyAttributes, resolveDirtyAttributes } from '../Style'
import styled, { css } from 'styled-components'

import { Tooltip, Flex } from '@sketch/components'

import { noop } from '@sketch/utils'
import { useCopiableArea } from '../CopiableText'
import { DirtyIconTooltip } from '../Style/DirtyIconTooltip'

/**
 * TYPES
 */
export interface HeaderProps {
  onCopy?: (value: string) => void
  copyValue?: string
  style?: CSSProperties
  className?: string
  children?: React.ReactNode
  dirtyAttributes?: DirtyAttributes
  /**
   * Allow for custom content on the right of the header when we want
   * something different from the usual dirtyAttributes tooltip.
   */
  extraRightContent?: React.ReactNode
}

/**
 * HELPER COMPONENTS
 */
const Container = styled.div.attrs({ className: 'header-container' })`
  display: flex;
  align-content: center;
  align-items: center;

  /* Separation between any content and a header container */
  /* stylelint-disable-next-line selector-type-no-unknown */
  *:not(&) + & {
    margin-top: 32px;
  }
`

const CopiableHeader = styled.div<{ canCopy?: boolean }>`
  cursor: ${({ canCopy }) => (canCopy ? 'pointer' : undefined)};
  width: 100%;

  > div {
    width: 100%;
  }
`

export const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.B};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-transform: uppercase;
  margin: 0;
  display: flex;
  justify-content: space-between;
`

export const SubTitle = styled.h2(
  ({ theme }) => css`
    display: flex;
    align-items: center;

    margin: 0;
    font-size: ${theme.fontSizes.D};
    font-weight: ${theme.fontWeights.medium};
    line-height: 1.538; /* stylelint-disable-line scales/line-heights */
    color: ${theme.colors.foreground.secondary.C};
  `
)

/**
 * MAIN COMPONENTS
 */
export const HeaderComponent: React.FCC<HeaderProps> = ({
  onCopy = noop,
  copyValue,
  style,
  className,
  dirtyAttributes,
  extraRightContent,
  children,
}) => {
  dirtyAttributes = resolveDirtyAttributes(dirtyAttributes)

  const { areaProps, tooltipProps, handleCopyValue, handleLeaveCopiableArea } =
    useCopiableArea(copyValue ?? '', { enabled: !!copyValue })

  const handleClickHeader = () => {
    if (!copyValue) {
      return
    }
    onCopy(copyValue)
    handleCopyValue()
  }

  return (
    <Container>
      <CopiableHeader
        {...areaProps}
        style={style}
        className={className}
        onClick={handleClickHeader}
        canCopy={!!copyValue}
      >
        <Tooltip {...tooltipProps}>{children}</Tooltip>
      </CopiableHeader>
      {dirtyAttributes && (
        <Flex marginTop={1}>
          <DirtyIconTooltip
            originalProperty={dirtyAttributes.originalProperty}
            originalValue={dirtyAttributes.originalValue}
            preventLabelTooltip={handleLeaveCopiableArea}
          />
        </Flex>
      )}
      {extraRightContent}
    </Container>
  )
}

export const Header = styled(HeaderComponent)<HeaderProps>`
  display: flex;
  align-items: center;
  margin-right: 8px;
  padding: 8px 0;

  color: ${({ theme }) => theme.colors.foreground.secondary.D};

  .CopyToClipboard {
    visibility: hidden;
  }
  &:hover .CopyToClipboard {
    visibility: visible;
  }
`

export const MiniHeader = styled(SubTitle)`
  display: flex;
  align-items: center;
  margin-right: 8px;
  padding: 8px 0;
`
